/* eslint-disable string-to-lingui/missing-lingui-transformation */

import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {httpBatchLink, httpLink, loggerLink, type Operation, splitLink} from '@trpc/client'
import React from 'react'

import {BffErrorMessages, bffHooks} from '@product-web/bff-client'
import {isTRPCClientError} from '@product-web/bff-client/errors'
import type {ClientContext} from '@product-web/bff-client/operational-context'
import {reportError} from '@product-web/error/report'
import {
    __getAccessToken,
    getScopedToken,
    memoForceSessionRefresh,
    useAccessToken,
    useEntitySwitchEffect,
} from '@product-web/shell/session'

import {getJwtExpiryMs} from '~/auth/jwt'
import {getTokenPayload} from '~/auth/token-data'
import config from 'config'

const TIMEOUT_RETRY_COUNT = 3

function shouldRetryQuery(failureCount: number, error: unknown) {
    // If a procedure times out, we retry it on the client side.
    // The reason why we don't retry timed out requests within the BFF is that we don't
    // want to delay all procedures that are batched along with the one that timed out.
    if (isTRPCClientError(error) && error.data?.code === 'TIMEOUT') {
        return failureCount <= TIMEOUT_RETRY_COUNT
    }
    // We retry requests to moons within the BFF
    return false
}

/**
 * A client-side provider for tRPC-based BFF (Backend-For-Frontend). This allows us
 * to use auto-generated SWR hooks to fetch data from our BFF layer. Refer to
 * app/bff for more context.
 */
export const BffProvider: React.FC = ({children}) => {
    const queryClient = React.useRef(
        new QueryClient({
            defaultOptions: {
                queries: {
                    retry: shouldRetryQuery,
                    // Override the default 1 second delay to attempt a retry.
                    // We only retry timeouts, and we already wait for a long time.
                    retryDelay: 0,
                    staleTime: 5 * (60 * 1000), // 5 mins
                    cacheTime: 10 * (60 * 1000), // 10 mins
                    // Call requests even when offline - necessary to run Cypress tests without internet connection
                    networkMode: window.Cypress ? 'always' : 'online',
                },
                mutations: {
                    retry: false,
                    // Call requests even when offline - necessary to run Cypress tests without internet connection
                    networkMode: window.Cypress ? 'always' : 'online',
                },
            },
        }),
    )

    const trpcClient = bffHooks.createClient({
        links: [
            loggerLink({enabled: () => config.environment === 'local'}),
            splitLink({
                condition: shouldEnableBatching,
                true: httpBatchLink({
                    url: removeTrailingSlash(config.endpoints.bff),
                    //  fix a limit to the batch size to avoid hitting the max URL length, the request will be split in multiple batches in case this limit is reached
                    maxURLLength: 10000,
                    async headers({opList}) {
                        return getHeaders(opList[0])
                    },
                }),
                false: httpLink({
                    url: removeTrailingSlash(config.endpoints.bff),
                    async headers({op}) {
                        return getHeaders(op)
                    },
                }),
            }),
        ],
    })

    return (
        <bffHooks.Provider client={trpcClient} queryClient={queryClient.current}>
            <EntitySwitchCacheBuster queryClient={queryClient.current} />
            <InvalidateQueries />
            <QueryClientProvider client={queryClient.current}>{children}</QueryClientProvider>
        </bffHooks.Provider>
    )
}

function removeTrailingSlash(str: string) {
    return str.replace(/\/+$/, '')
}

// When the access token changes we need to invalidate all data fetched.
function InvalidateQueries() {
    const accessToken = useAccessToken()
    const utils = bffHooks.useUtils()

    React.useEffect(() => {
        utils.invalidate().catch(reportError)
    }, [accessToken, utils])

    return null
}

/**
 * Often BFF queries does not include entity-specific input parameters like company ID
 * or user ID. However, query data usually is entity-specific.
 * So when user switches between accounts/entities BFF query returns cached data for the previous entity
 * since BFF cache key does not include entity-specific information.
 *
 * We run query revalidation on every accessToken change (see InvalidateQueries above)
 * so eventually after a couple of seconds data will be synchronized with the new entity
 * but revalidation takes some time and user will see unrelated data until revalidation is finished.
 *
 * To avoid this we are clearing BFF cache entirely on account/entity switch so developers
 * could implement proper loading indication on account/entity switch.
 *
 * If BFF query is not specific to the entity or it is desired behaviour to keep query data between entity switches
 * just add `keepPreviousData: true` option to the BFF query and it will survive cache clearing.
 * Note that queries with `keepPreviousData: true` still will be revalidated.
 */
function EntitySwitchCacheBuster({queryClient}: {queryClient: QueryClient}) {
    useEntitySwitchEffect(() => {
        queryClient.getQueryCache().clear()
    })

    return null
}

/**
 * Truthy return values will enable query batching.
 * Always skip batching if:
 * - we're mocking requests (https://linear.app/pleo/issue/WEB-256/enable-query-batch-in-e2e-tests)
 * - when using an elevated auth token
 * - if we specifically request a non batched request via `context.skipBatch` flag
 */
function shouldEnableBatching(op: Operation) {
    const {auth, skipBatch: skipBatchFromContext} = op.context as ClientContext

    const useElevatedAuth = !!(auth?.type === 'elevated')
    const isMockingRequests = !!window.Cypress || window.___msw?.active

    const skipBatching = isMockingRequests || useElevatedAuth || !!skipBatchFromContext
    return !skipBatching
}

/**
 * Prepare the operational context for the final request.
 * Based on the operational context and environment, we may need to:
 * - get a new elevated scoped token
 */
async function getAccessToken(op: Operation) {
    const {auth} = op.context as ClientContext

    if (!auth) {
        // No specific auth context specified, we use the current access token.
        // First we make sure it's fresh, if not we refresh it first.
        await refreshTokenIfNeeded()
        return __getAccessToken()
    }

    if (auth.type === 'elevated') {
        // If this is an elevated auth request, we need to get a new scoped token.
        // Note that this will likely show a PIN modal.
        const {elevatedResourceIds, forcePIN, scope} = auth
        const accessToken =
            (await getScopedToken(scope, {
                force: forcePIN,
                elevatedResourceIds,
            })) || null

        if (accessToken === null) {
            throw new Error(BffErrorMessages.PIN_MODAL_CANCELLED)
        }

        return accessToken
    }

    if (auth.type === 'token') {
        // If this is a request with a specific token, we use that token.
        return auth.accessToken
    }

    throw new Error(`Unknown auth type, please use the defineClientContext helper`)
}

async function getHeaders(operation: Operation) {
    const token = await getAccessToken(operation)
    const ajsHeaders = getAjsHeaders()
    return token ? {Authorization: `Bearer ${token}`, ...ajsHeaders} : ajsHeaders
}

/**
 * Get the anonymousId from Analytics JS, if available.
 * This will be added to the headers so that we can identify and track anonymous users in the BFF.
 * More info on Segment Identify: https://segment.com/docs/connections/spec/best-practices-identify/
 */
function getAjsHeaders() {
    const anonymousId = window.analytics?.user?.().anonymousId()
    if (anonymousId) {
        return {['ajs-anonymous-id']: anonymousId}
    }
    return {}
}

/**
 * Check if the token is expired or about to expire. If so, force refresh the
 * session to acquire a new token.
 */
async function refreshTokenIfNeeded() {
    // 20s is the timeout on our BFF lambda, which means that with 25s here,
    // even in the worst case scenario the token won't expire while the
    // BFF call is in progress, and we should never see 401s coming back.
    const MIN_TOKEN_TTL_MS = 25000

    const accessToken = __getAccessToken()
    if (!accessToken) {
        return
    }
    const tokenPayload = getTokenPayload(accessToken)
    if (!tokenPayload || getJwtExpiryMs(tokenPayload) < MIN_TOKEN_TTL_MS) {
        // We only want to trigger the session refresh once, even if we're dealing with
        // multiple requests triggered at the same time
        await memoForceSessionRefresh()
    }
}
